import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { API_BASE } from "../api/config";
import LocationSelect from "../components/LocationSelect";
import "./PhysicalStockCountPanel.css";

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function formatQty(value) {
  return Number(value || 0).toLocaleString();
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString();
}

function cleanCode(value) {
  return String(value || "").trim().toUpperCase();
}

const PRETTYFIT_SIZES = ["35", "36", "37", "38", "39", "40"];

const HANGTEN_SIZES = [
  "24", "25", "26", "27", "28", "29", "30", "31", "32", "34", "36", "38",
  "XS", "S", "M", "L", "XL", "XXL",
];

function sizeColumnsForBrand(brand) {
  return cleanCode(brand) === "PRETTYFIT" ? PRETTYFIT_SIZES : HANGTEN_SIZES;
}

export default function PhysicalStockCountPanel({ onApplied }) {
  const [location, setLocation] = useState("GMP");
  const [brand, setBrand] = useState("PRETTYFIT");
  const [countDate, setCountDate] = useState(todayKey());
  const [batchNo, setBatchNo] = useState("");
  const [note, setNote] = useState("Go-live physical stock count");
  const [fileName, setFileName] = useState("");
  const [sheetRows, setSheetRows] = useState([]);

  const [previewRows, setPreviewRows] = useState([]);
  const [previewSummary, setPreviewSummary] = useState(null);
  const [warnings, setWarnings] = useState([]);

  const [batches, setBatches] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionId, setActionId] = useState("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  function autoBatchNo() {
    return `PHYSICAL-STOCK-${location}-${brand}-${countDate}`;
  }

  async function loadBatches() {
    try {
      const query = new URLSearchParams();
      query.set("status", statusFilter);
      query.set("locationCode", "ALL");
      query.set("brand", "ALL");

      const response = await fetch(`${API_BASE}/api/erp/physical-stock-counts?${query.toString()}`);
      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to load physical count batches");
      }

      setBatches(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      setError(err.message);
    }
  }

  function normalizeHeader(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[_-]/g, "");
  }

  function canonicalHeader(value) {
    const raw = String(value || "").trim();
    const h = normalizeHeader(raw);

    if (["code", "productcode", "product"].includes(h)) return "code";
    if (["color", "colour", "col"].includes(h)) return "color";
    if (["price", "sellingprice", "pricemmk"].includes(h)) return "price";

    return raw.toUpperCase();
  }

  async function readExcelFile(file) {
    setError("");
    setSuccessMsg("");
    setPreviewRows([]);
    setPreviewSummary(null);
    setWarnings([]);

    if (!file) return;

    setFileName(file.name);

    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });
    const firstSheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[firstSheetName];

    const rawRows = XLSX.utils.sheet_to_json(sheet, {
      header: 1,
      defval: "",
      raw: false,
    });

    const headerIndex = rawRows.findIndex((row) => {
      const headers = row.map(canonicalHeader);
      return headers.includes("code") && headers.includes("color");
    });

    if (headerIndex === -1) {
      setSheetRows([]);
      setError("Excel header row ???????? Header ??? code ????? color column ???????");
      return;
    }

    const headers = rawRows[headerIndex].map(canonicalHeader);

    const validRows = rawRows
      .slice(headerIndex + 1)
      .map((row) => {
        const obj = {};

        headers.forEach((header, index) => {
          if (!header) return;
          obj[header] = row[index] ?? "";
        });

        return obj;
      })
      .filter((row) => String(row.code || "").trim());

    setSheetRows(validRows);
    setSuccessMsg(
      `${validRows.length} Excel rows loaded from ${file.name}. Header row: ${headerIndex + 1}`
    );
  }

  async function dryRun() {
    try {
      setLoading(true);
      setError("");
      setSuccessMsg("");

      if (!location || location === "ALL") {
        throw new Error("Please select one location. Physical count cannot use ALL.");
      }

      if (!brand || brand === "ALL") {
        throw new Error("Please select brand.");
      }

      if (sheetRows.length === 0) {
        throw new Error("Please upload Excel sheet first.");
      }

      const response = await fetch(`${API_BASE}/api/erp/physical-stock-counts/dry-run`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          locationCode: location,
          brand,
          rows: sheetRows,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Dry run failed");
      }

      setPreviewRows(Array.isArray(data.rows) ? data.rows : []);
      setPreviewSummary(data.summary || null);
      setWarnings(Array.isArray(data.warnings) ? data.warnings : []);
      setSuccessMsg("Dry run finished. No data saved yet.");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function saveDraft() {
    try {
      setSaving(true);
      setError("");
      setSuccessMsg("");

      if (!location || location === "ALL") {
        throw new Error("Please select one location.");
      }

      if (!brand || brand === "ALL") {
        throw new Error("Please select brand.");
      }

      if (sheetRows.length === 0) {
        throw new Error("Please upload Excel sheet first.");
      }

      const finalBatchNo = batchNo.trim() || autoBatchNo();

      const response = await fetch(`${API_BASE}/api/erp/physical-stock-counts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          locationCode: location,
          brand,
          countDate,
          batchNo: finalBatchNo,
          note,
          createdBy: "OFFICE",
          rows: sheetRows,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Save draft failed");
      }

      setBatchNo(finalBatchNo);
      setSuccessMsg(`Draft saved: ${finalBatchNo}`);
      await loadBatches();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function approveBatch(row) {
    try {
      setActionId(row.id);
      setError("");
      setSuccessMsg("");

      const response = await fetch(`${API_BASE}/api/erp/physical-stock-counts/${row.id}/approve`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          approvedBy: "ADMIN",
        }),
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Approve failed");
      }

      setSuccessMsg(`Approved: ${row.batchNo}`);
      await loadBatches();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionId("");
    }
  }

  async function applyBatch(row) {
    try {
      setActionId(row.id);
      setError("");
      setSuccessMsg("");

      const response = await fetch(`${API_BASE}/api/erp/physical-stock-counts/${row.id}/apply`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          appliedBy: "ADMIN",
          applyNote: "Apply physical stock count to ERP ledger",
          movementDate: countDate,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Apply failed");
      }

      setSuccessMsg(`Applied: ${row.batchNo} / Rows: ${data.summary?.insertedRows || 0}`);
      await loadBatches();

      if (typeof onApplied === "function") {
        onApplied();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionId("");
    }
  }

  useEffect(() => {
    setBatchNo(autoBatchNo());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, brand, countDate]);

  useEffect(() => {
    loadBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const sizeColumns = sizeColumnsForBrand(brand);

  const matrixPreviewRows = useMemo(() => {
    const map = new Map();

    previewRows.forEach((row) => {
      const key = `${row.productCode}|||${row.color || ""}`;

      if (!map.has(key)) {
        map.set(key, {
          productCode: row.productCode,
          color: row.color || "",
          sizes: {},
          currentTotal: 0,
          countedTotal: 0,
          diffTotal: 0,
          priceMmk: Number(row.priceMmk || 0),
        });
      }

      const item = map.get(key);
      const size = String(row.size || "").trim();

      item.sizes[size] = {
        currentQty: Number(row.currentQty || 0),
        countedQty: Number(row.countedQty || 0),
        diffQty: Number(row.diffQty || 0),
      };

      item.currentTotal += Number(row.currentQty || 0);
      item.countedTotal += Number(row.countedQty || 0);
      item.diffTotal += Number(row.diffQty || 0);
      item.priceMmk = item.priceMmk || Number(row.priceMmk || 0);
    });

    return Array.from(map.values()).sort((a, b) =>
      String(a.productCode).localeCompare(String(b.productCode)) ||
      String(a.color).localeCompare(String(b.color))
    );
  }, [previewRows]);

  return (
    <div className="physicalStockPanel">
      <div className="physicalStockPanel__head">
        <div>
          <div className="physicalStockPanel__tag">GO-LIVE PHYSICAL COUNT</div>
          <h3>Physical Stock Count Import</h3>
          <p>
            Shop ground stock အမှန်ကို Excel sheet နဲ့ ERP ထဲ Draft → Approve → Apply လုပ်ရန်။
            Firebase မပြင်သေးပါ။
          </p>
        </div>

        <button type="button" onClick={loadBatches}>
          Refresh Batches
        </button>
      </div>

      <div className="physicalStockPanel__format">
        {brand === "PRETTYFIT" ? (
          <span>PrettyFit format: code | color | 35 | 36 | 37 | 38 | 39 | 40 | price</span>
        ) : (
          <span>
            Hangten format: code | color | 24 | 25 | 26 | 27 | 28 | 29 | 30 | 31 | 32 | 34 | 36 | 38 | XS | S | M | L | XL | XXL | price
          </span>
        )}
      </div>

      <div className="physicalStockPanel__filters">
        <label>
          <span>Location</span>
          <LocationSelect
            value={location}
            onChange={setLocation}
            type="ALL"
            includeAll={false}
          />
        </label>

        <label>
          <span>Brand</span>
          <select value={brand} onChange={(e) => setBrand(e.target.value)}>
            <option value="PRETTYFIT">PRETTYFIT</option>
            <option value="HANGTEN">HANGTEN</option>
          </select>
        </label>

        <label>
          <span>Count Date</span>
          <input
            type="date"
            value={countDate}
            onChange={(e) => setCountDate(e.target.value)}
          />
        </label>

        <label>
          <span>Batch No</span>
          <input
            value={batchNo}
            onChange={(e) => setBatchNo(e.target.value)}
          />
        </label>
      </div>

      <div className="physicalStockPanel__uploadRow">
        <label className="physicalStockPanel__file">
          <span>Upload Excel Sheet</span>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => readExcelFile(e.target.files?.[0])}
          />
        </label>

        <label>
          <span>Note</span>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>

        <button type="button" onClick={dryRun} disabled={loading}>
          {loading ? "Checking..." : "Dry Run"}
        </button>

        <button type="button" onClick={saveDraft} disabled={saving}>
          {saving ? "Saving..." : "Save Draft"}
        </button>
      </div>

      {fileName && (
        <div className="physicalStockPanel__meta">
          File: <b>{fileName}</b> / Excel Rows: <b>{sheetRows.length}</b>
        </div>
      )}

      {error && <div className="physicalStockPanel__error">{error}</div>}
      {successMsg && <div className="physicalStockPanel__success">{successMsg}</div>}

      {previewSummary && (
        <div className="physicalStockPanel__summaryCards">
          <div>
            <span>Normalized Rows</span>
            <b>{formatQty(previewSummary.normalizedRows)}</b>
          </div>
          <div>
            <span>Change Rows</span>
            <b>{formatQty(previewSummary.changeRows)}</b>
          </div>
          <div>
            <span>Total Counted Qty</span>
            <b>{formatQty(previewSummary.totalCountedQty)}</b>
          </div>
          <div>
            <span>Total Diff Qty</span>
            <b>{formatQty(previewSummary.totalDiffQty)}</b>
          </div>
        </div>
      )}

      {warnings.length > 0 && (
        <div className="physicalStockPanel__warnings">
          {warnings.slice(0, 8).map((warning, index) => (
            <div key={index}>⚠️ {warning}</div>
          ))}
        </div>
      )}

      <div className="physicalStockTableWrap">
        <table className="physicalStockTable">
          <thead>
            <tr>
              <th>Code</th>
              <th>Color</th>
              {sizeColumns.map((size) => (
                <th key={size}>{size}</th>
              ))}
              <th>Current</th>
              <th>Counted</th>
              <th>Diff</th>
              <th>Price</th>
            </tr>
          </thead>

          <tbody>
            {previewRows.length === 0 ? (
              <tr>
                <td colSpan={sizeColumns.length + 6} className="physicalStockTable__empty">
                  Upload Excel and click Dry Run to preview.
                </td>
              </tr>
            ) : (
              matrixPreviewRows.slice(0, 300).map((row, index) => (
                <tr key={`${row.productCode}-${row.color}-${index}`}>
                  <td className="physicalStockTable__code">{row.productCode}</td>
                  <td>{row.color || "-"}</td>

                  {sizeColumns.map((size) => {
                    const sizeData = row.sizes?.[size];
                    const hasSize = Boolean(sizeData);
                    const qty = hasSize ? Number(sizeData.countedQty || 0) : null;

                    return (
                      <td
                        key={size}
                        className={
                          hasSize && qty > 0
                            ? "physicalStockTable__hit"
                            : hasSize
                              ? "physicalStockTable__zero"
                              : ""
                        }
                      >
                        {hasSize ? formatQty(qty) : "-"}
                      </td>
                    );
                  })}

                  <td>{formatQty(row.currentTotal)}</td>
                  <td>{formatQty(row.countedTotal)}</td>
                  <td className={Number(row.diffTotal || 0) >= 0 ? "physicalStockTable__in" : "physicalStockTable__out"}>
                    {formatQty(row.diffTotal)}
                  </td>
                  <td>{formatMoney(row.priceMmk)} Ks</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="physicalStockPanel__batchHead">
        <h4>Physical Count Batches</h4>

        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="ALL">ALL</option>
          <option value="DRAFT">DRAFT</option>
          <option value="APPROVED">APPROVED</option>
          <option value="APPLIED">APPLIED</option>
        </select>
      </div>

      <div className="physicalBatchTableWrap">
        <table className="physicalBatchTable">
          <thead>
            <tr>
              <th>Batch No</th>
              <th>Location</th>
              <th>Brand</th>
              <th>Status</th>
              <th>Rows</th>
              <th>Total Qty</th>
              <th>Created By</th>
              <th>Created</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {batches.length === 0 ? (
              <tr>
                <td colSpan="9" className="physicalBatchTable__empty">
                  No physical stock count batches.
                </td>
              </tr>
            ) : (
              batches.map((row) => (
                <tr key={row.id}>
                  <td className="physicalBatchTable__batch">{row.batchNo}</td>
                  <td>{row.locationCode}</td>
                  <td>{row.brand}</td>
                  <td>
                    <span className={`physicalBatchStatus physicalBatchStatus--${String(row.status).toLowerCase()}`}>
                      {row.status}
                    </span>
                  </td>
                  <td>{formatQty(row.itemRows)}</td>
                  <td>{formatQty(row.totalCountedQty)}</td>
                  <td>{row.createdBy || "-"}</td>
                  <td>{row.createdAt ? new Date(row.createdAt).toLocaleString() : "-"}</td>
                  <td>
                    {row.status === "DRAFT" ? (
                      <button
                        type="button"
                        className="physicalBatchTable__approve"
                        onClick={() => approveBatch(row)}
                        disabled={actionId === row.id}
                      >
                        Approve
                      </button>
                    ) : row.status === "APPROVED" ? (
                      <button
                        type="button"
                        className="physicalBatchTable__apply"
                        onClick={() => applyBatch(row)}
                        disabled={actionId === row.id}
                      >
                        Apply
                      </button>
                    ) : (
                      <span className="physicalBatchTable__done">Done</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
